import { listProductBundleItems } from "./catalog";

export type KitLineInput = { product_id: string; qty_per_kit: number };

/** Merge kit lines by product_id (sum quantities). */
export function mergeKitLines(existing: KitLineInput[], added: KitLineInput[]): KitLineInput[] {
  const byId = new Map<string, number>();
  for (const line of [...existing, ...added]) {
    const qty = Math.max(1, Math.floor(Number(line.qty_per_kit)) || 1);
    byId.set(line.product_id, (byId.get(line.product_id) ?? 0) + qty);
  }
  return Array.from(byId.entries()).map(([product_id, qty_per_kit]) => ({ product_id, qty_per_kit }));
}

/**
 * Expand a product into leaf kit lines (bundles → components, recursively).
 * Non-bundle products return a single line.
 */
export async function expandProductToKitLines(
  productId: string,
  qtyMultiplier: number,
  visited = new Set<string>()
): Promise<KitLineInput[]> {
  if (visited.has(productId)) return [];
  visited.add(productId);

  const mult = Math.max(1, Math.floor(Number(qtyMultiplier)) || 1);
  const items = await listProductBundleItems(productId).catch(() => []);

  if (items.length === 0) {
    return [{ product_id: productId, qty_per_kit: mult }];
  }

  const out: KitLineInput[] = [];
  for (const item of items) {
    const perUnit = item.qty_used ?? item.quantity ?? item.default_qty ?? 1;
    const childQty = perUnit * mult;
    const childLines = await expandProductToKitLines(item.component_product_id, childQty, visited);
    out.push(...childLines);
  }
  return out;
}

/** Expand all lines in a kit snapshot (legacy bundle refs → leaf parts). */
export async function expandKitLinesSnapshot(lines: KitLineInput[]): Promise<KitLineInput[]> {
  let merged: KitLineInput[] = [];
  for (const line of lines) {
    const expanded = await expandProductToKitLines(line.product_id, line.qty_per_kit);
    merged = mergeKitLines(merged, expanded);
  }
  return merged;
}
